import React from "react";
import { Box, CircularProgress, Tab, Tabs, TabList, TabPanel } from "@mui/joy";
import CommonModal from "../../common/CommonModal";
import Pagination from "../../common/Pagination";
import { useCustomer } from "../../../contexts/api";
import type { ZekayCustomer } from "../../../api/customers/Customer.typed";
import ProfileFormEdit from "../profileForm/display/infos/ProfileFormEdit";
import ProfileFormView from "../profileForm/display/infos/ProfileFormView";
import { useProfileForm } from "../profileForm/hooks/useProfileForm";
import CustomerExcursionsTab from "../customerExcursions/CustomerExcursionsTab";
import { useCustomerExcursions } from "../customerExcursions/useCustomerExcursions";
import CustomerCountersTab from "../customerProducts/CustomerCountersTab";
import CustomerTrainingsTab from "../customerTrainings/CustomerTrainingsTab";
import {
  colorTokens,
  zekayModalTabsRootSx,
  zekayModalTabListSx,
  zekayModalTabPanelSx,
} from "../../../styles/globalStyles";
import { useCustomersModulePermissions } from "../../customers/hooks/useCustomersModulePermissions";

export type CustomerOverviewTabSlug = "profile" | "excursions" | "products" | "trainings";

type Props = {
  open: boolean;
  onClose: () => void;
  customerId: number;
  desync: boolean;
  editable?: boolean;
  onCustomerUpdated?: (customer: ZekayCustomer) => void;
};

function firstAvailableTabSlug(perms: {
  profile: boolean;
  excursions: boolean;
  products: boolean;
  trainings: boolean;
}): CustomerOverviewTabSlug | null {
  if (perms.profile) return "profile";
  if (perms.excursions) return "excursions";
  if (perms.products) return "products";
  if (perms.trainings) return "trainings";
  return null;
}

export default function CustomerOverviewModal({
  open,
  onClose,
  customerId,
  desync,
  editable = false,
  onCustomerUpdated,
}: Props) {
  const { getCustomer } = useCustomer();
  const { canOpenProfile, canOpenTrainingsModal, canOpenExcursionsModal, canOpenCustomerProducts } =
    useCustomersModulePermissions(desync);

  const [customer, setCustomer] = React.useState<ZekayCustomer | undefined>();
  const [loadingCustomer, setLoadingCustomer] = React.useState(false);
  const [tab, setTab] = React.useState<CustomerOverviewTabSlug>("profile");

  const perms = React.useMemo(
    () => ({
      profile: canOpenProfile,
      excursions: canOpenExcursionsModal,
      products: canOpenCustomerProducts,
      trainings: canOpenTrainingsModal,
    }),
    [canOpenProfile, canOpenExcursionsModal, canOpenCustomerProducts, canOpenTrainingsModal],
  );

  const canOpenAnything = perms.profile || perms.excursions || perms.products || perms.trainings;

  const setProfileFormOpen = React.useCallback(
    (v: React.SetStateAction<boolean>) => {
      const resolved = typeof v === "function" ? v(true) : v;
      if (!resolved) onClose();
    },
    [onClose],
  );

  const setCustomerUpdated = React.useCallback(
    (action: React.SetStateAction<ZekayCustomer | undefined>) => {
      const next = typeof action === "function" ? action(customer) : action;
      if (next != null) {
        setCustomer(next);
        onCustomerUpdated?.(next);
      }
    },
    [customer, onCustomerUpdated],
  );

  React.useEffect(() => {
    if (!open) {
      setCustomer(undefined);

      return;
    }
    setLoadingCustomer(true);
    void getCustomer(String(customerId))
      .then((c) => setCustomer(c ?? undefined))
      .finally(() => {
        setLoadingCustomer(false);
      });
  }, [open, customerId, getCustomer]);

  React.useEffect(() => {
    if (!open) return;
    const first = firstAvailableTabSlug(perms);
    if (first != null) setTab(first);
  }, [open, perms.profile, perms.excursions, perms.products, perms.trainings]);

  const effectiveEditable = Boolean(editable || desync === true);
  const showQualificationSelect = desync === false ? false : desync === true || effectiveEditable;

  const {
    form,
    setForm,
    qualificationItems,
    saving,
    canEditProfile,
    showFooter,
    saveValid,
    confirmLabel,
    associateProductIds,
    setAssociateProductIds,
    handleSave,
  } = useProfileForm({
    customer,
    setProfileFormOpen,
    setCustomerUpdated,
    editable: effectiveEditable,
  });

  const exc = useCustomerExcursions(open && tab === "excursions" ? customerId : undefined);

  const customerNameLabel = customer
    ? [customer.FirstName, customer.LastName].filter(Boolean).join(" ").trim()
    : undefined;
  const title = customerNameLabel || "Client";

  if (!canOpenAnything) return null;

  return (
    <CommonModal
      open={open}
      onClose={onClose}
      title={title}
      size="xl"
      onConfirm={tab === "profile" && showFooter ? handleSave : undefined}
      confirmLabel={confirmLabel}
      submitDisabled={!saveValid || saving || loadingCustomer || !customer}
      loading={saving || loadingCustomer}
    >
      {!customer || loadingCustomer ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress
            sx={{
              "--CircularProgress-trackColor": colorTokens.border.default,
              "--CircularProgress-progressColor": colorTokens.accent.primary,
            }}
          />
        </Box>
      ) : (
        <Tabs
          value={tab}
          onChange={(_, v) => v != null && setTab(v as CustomerOverviewTabSlug)}
          sx={zekayModalTabsRootSx}
        >
          <TabList sx={zekayModalTabListSx}>
            {perms.profile ? <Tab value="profile">Profil</Tab> : null}
            {perms.excursions ? <Tab value="excursions">Sorties</Tab> : null}
            {perms.products ? <Tab value="products">Produits</Tab> : null}
            {perms.trainings ? <Tab value="trainings">Formations</Tab> : null}
          </TabList>
          {perms.profile ? (
            <TabPanel value="profile" sx={zekayModalTabPanelSx}>
              {effectiveEditable ? (
                <ProfileFormEdit
                  form={form}
                  setForm={setForm}
                  customer={customer}
                  canEditProfile={canEditProfile}
                  setCustomerUpdated={setCustomerUpdated}
                  desync={showQualificationSelect}
                  qualificationItems={qualificationItems}
                  showCustomerProducts={false}
                  showAssociateProductInCreate={false}
                  associateProductIds={associateProductIds}
                  setAssociateProductIds={setAssociateProductIds}
                />
              ) : (
                <ProfileFormView
                  customer={customer}
                  canEditProfile={canEditProfile}
                  setCustomerUpdated={setCustomerUpdated}
                />
              )}
            </TabPanel>
          ) : null}
          {perms.excursions ? (
            <TabPanel value="excursions" sx={zekayModalTabPanelSx}>
              <CustomerExcursionsTab
                excursions={exc.excursions}
                loading={exc.loading}
                totalItems={exc.totalItems}
              />
              {exc.totalPages > 0 && (
                <Box sx={{ flexShrink: 0, mt: 1 }}>
                  <Pagination
                    currentPage={exc.currentPage}
                    totalPages={exc.totalPages}
                    totalItems={exc.totalItems}
                    itemsPerPage={exc.itemsPerPage}
                    onPageChange={exc.handlePageChange}
                    onItemsPerPageChange={exc.handleItemsPerPageChange}
                    loading={exc.loading}
                  />
                </Box>
              )}
            </TabPanel>
          ) : null}
          {perms.products && customer.Id != null ? (
            <TabPanel value="products" sx={zekayModalTabPanelSx}>
              <CustomerCountersTab customerId={customer.Id} />
            </TabPanel>
          ) : null}
          {perms.trainings && customer.Id != null ? (
            <TabPanel value="trainings" sx={zekayModalTabPanelSx}>
              <CustomerTrainingsTab customerId={customer.Id} />
            </TabPanel>
          ) : null}
        </Tabs>
      )}
    </CommonModal>
  );
}
